// src/App.js
import { useState, useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  MsalProvider,
  useMsal,
} from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import LoadingScreen from "./components/common/LoadingScreen";
import { defaultResources } from "./utils/resourceLoader";
import useResponsiveFontSize from "./hooks/useResponsiveFontSize";
import Cookies from "js-cookie";
import axios from "axios";
import "./styles/global.css";

// Pages
import HomePage from "./pages/Home/Home";
import AuthPage from "./pages/Auth/Auth";
import Manage from "./pages/Control/Manage";

// 创建 MSAL 实例
const msalInstance = new PublicClientApplication(msalConfig);

// 监听登录事件，设置活动账户
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    console.log(
      "Login success, setting active account:",
      event.payload.account.username,
    );
    msalInstance.setActiveAccount(event.payload.account);
  }
});

// 重定向回调处理组件
const RedirectHandler = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("RedirectHandler: 处理重定向回调");

    // 处理重定向响应
    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          console.log("重定向登录成功:", response);
          instance.setActiveAccount(response.account);
          // 已经在 /auth 页面，不需要导航
        }
      })
      .catch((error) => {
        console.error("重定向处理失败:", error);
      });
  }, [instance, navigate]);

  return null; // 这个组件不渲染任何内容
};

// 基于Cookie的受保护路由组件
const CookieProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null 表示正在检查
  const navigate = useNavigate();
  const BASE_URL = "https://api1.ororigin.xyz";

  const checkUserLogin = async (userUid) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/user/${userUid}`);
      if (response.data.success && response.data.user.is_active) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("用户验证失败:", error);
      return false;
    }
  };

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const uid = Cookies.get("uid");
        
        if (!uid) {
          console.log("未找到用户UID Cookie");
          setIsAuthenticated(false);
          navigate("/auth")
          return;
        }

        const isValid = await checkUserLogin(uid);
        setIsAuthenticated(isValid);
        
        if (!isValid) {
          // 清除无效的Cookie
          Cookies.remove("uid");
          // 可以重定向到登录页面或其他页面
          navigate("/auth");
        }
      } catch (error) {
        console.error("验证用户时出错:", error);
        setIsAuthenticated(false);
        navigate("/auth");
      }
    };

    verifyUser();
  }, [navigate]);

  // 显示加载状态
  if (isAuthenticated === null) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">正在验证用户...</p>
        </div>
      </div>
    );
  }

  // 如果验证失败，不渲染子组件，useEffect已经处理了重定向
  if (!isAuthenticated) {
    return null;
  }

  // 验证通过，渲染子组件
  return children;
};

function App() {
  useResponsiveFontSize(16);
  const [isLoading, setIsLoading] = useState(true);
  const [enableLoadingScreen, setEnableLoadingScreen] = useState(true);
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);

  // 初始化 MSAL
  useEffect(() => {
    console.log("开始初始化 MSAL...");

    msalInstance
      .initialize()
      .then(() => {
        console.log("MSAL 初始化成功");
        setIsMsalInitialized(true);
      })
      .catch((error) => {
        console.error("MSAL 初始化失败:", error);
        setIsMsalInitialized(true); // 即使失败也继续
      });
  }, []);

  const handleLoadingComplete = () => {
    console.log("资源预加载完成");
    const links = document.querySelectorAll('link[rel="preload"][as="style"]');
    links.forEach((link) => {
      link.rel = "stylesheet";
    });
    setEnableLoadingScreen(false);
  };

  const handleContentPreload = () => {
    setIsLoading(false);
  };

  // 等待 MSAL 初始化完成
  if (!isMsalInitialized) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">正在初始化身份验证...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <Routes>
          {/* 重定向处理路由 - 处理微软回调 */}
          <Route
            path="/auth"
            element={
              <>
                <RedirectHandler />
                <AuthPage />
              </>
            }
          />

          {/* 受保护的管理页面 */}
          <Route
            path="/control-panel"
            element={
              <CookieProtectedRoute>
                <Manage />
              </CookieProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              <>
                {enableLoadingScreen && (
                  <LoadingScreen
                    onLoadingCompleteAnimation={handleContentPreload}
                    onLoadingComplete={handleLoadingComplete}
                    minDisplayTime={1200}
                    resources={defaultResources}
                  />
                )}
                <HomePage isLoading={isLoading} />
              </>
            }
          />

          {/* 重定向未知路由到首页 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;